import { readConditions } from '../conditions';

const PLACEHOLDER = /^\$\{\{\s*[a-zA-Z0-9_.-]+\s*\}\}$/;
const INPUT_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

export default function validate(workflow, catalog) {
  const found = { steps: {}, triggers: {} };
  const keys = workflow.variables.map((variable) => variable.key.trim());
  const inputs = inputProblem(workflow.inputs, catalog.messages);

  if (!workflow.name.trim()) found.name = catalog.messages.name;

  for (const trigger of workflow.triggers) {
    const problems = triggerProblems(trigger, catalog);

    if (Object.keys(problems).length > 0) found.triggers[trigger.id] = problems;
  }

  if (keys.some((key) => !key)) found.variables = catalog.messages.variableKey;
  else if (new Set(keys).size !== keys.length) found.variables = catalog.messages.variableDuplicated;

  if (inputs) found.inputs = inputs;

  const seen = [];

  for (const step of workflow.steps) {
    const problems = stepProblems(step, seen, catalog);

    if (step.key.trim()) seen.push(step.key.trim());
    if (Object.keys(problems).length > 0) found.steps[step.id] = problems;
  }

  return found;
}

function inputProblem(inputs, messages) {
  const keys = inputs.map((input) => input.key.trim());

  if (keys.some((key) => !key)) return messages.inputKey;
  if (keys.some((key) => !INPUT_KEY_PATTERN.test(key))) return messages.inputKeyInvalid;
  if (new Set(keys).size !== keys.length) return messages.inputDuplicated;
  if (inputs.some((input) => !input.label.trim())) return messages.inputLabel;
  if (inputs.some((input) => input.kind === 'select' && !input.options.trim())) return messages.inputOptions;

  return null;
}

function triggerProblems(trigger, catalog) {
  const problems = {};

  if (trigger.kind === 'cron' && !/^\S+( \S+){4}$/.test(trigger.cron.trim())) {
    problems.cron = catalog.messages.cron;
  }

  if (trigger.kind === 'webhook' && !trigger.webhookId) problems.webhook = catalog.messages.webhook;
  if (trigger.kind === 'webhook') weighConditions(trigger, problems, catalog, catalog.triggerOperators);

  return problems;
}

function stepProblems(step, seen, catalog) {
  const problems = {};
  const key = step.key.trim();

  if (!step.name.trim()) problems.name = catalog.messages.stepName;
  if (!key) problems.key = catalog.messages.stepKey;
  else if (seen.includes(key)) problems.key = catalog.messages.stepKeyDuplicated;

  if (step.mode !== 'workflow') weighBundles(step, problems, catalog.messages);
  if (step.mode === 'script' && !step.command.trim()) problems.command = catalog.messages.stepCommand;
  if (step.mode === 'agent' && !step.prompt.trim()) problems.prompt = catalog.messages.stepPrompt;

  if (step.mode === 'agent' && step.mcpServers.some((attachment) => !attachment.mcpServerId)) {
    problems.mcpServers = catalog.messages.stepMcpServerBlank;
  } else if (step.mode === 'agent' && duplicated(step.mcpServers, 'mcpServerId')) {
    problems.mcpServers = catalog.messages.stepMcpServerDuplicated;
  }

  if (step.mode === 'workflow') weighChildWorkflow(step, problems, catalog.messages);

  weighConditions(step, problems, catalog, catalog.stepOperators);

  return problems;
}

function weighBundles(step, problems, messages) {
  if (step.mode === 'script' && step.bundles.length === 0) problems.bundles = messages.stepBundle;
  else if (step.bundles.some((attachment) => !attachment.bundleId)) problems.bundles = messages.stepBundleBlank;
  else if (duplicated(step.bundles, 'bundleId')) problems.bundles = messages.stepBundleDuplicated;
}

function weighChildWorkflow(step, problems, messages) {
  if (!step.childWorkflowId) problems.childWorkflow = messages.stepChildWorkflow;
  if (step.childInputs.trim() && !readObject(step.childInputs)) problems.childInputs = messages.stepChildInputs;
  if (step.each.trim() && !PLACEHOLDER.test(step.each.trim())) problems.each = messages.stepEach;
}

function weighConditions(entity, problems, catalog, known) {
  const conditions = readConditions(entity.conditions);

  if (conditions === null) {
    problems.conditions = catalog.messages.conditions;

    return;
  }

  const unknown = unknownOperators(conditions, known);

  if (unknown.length === 0) return;

  problems.conditions = catalog.messages.conditionsUnknownOperator.replace('%{operators}', unknown.join(', '));
}

function unknownOperators(node, known) {
  if (Array.isArray(node)) return node.flatMap((branch) => unknownOperators(branch, known));
  if (node === null || typeof node !== 'object') return [];

  return Object.entries(node).flatMap(([key, value]) => {
    if (key.startsWith('$') && !known.includes(key)) return [key];

    return unknownOperators(value, known);
  });
}

function duplicated(attachments, field) {
  const chosen = attachments.map((attachment) => attachment[field]).filter(Boolean);

  return new Set(chosen).size !== chosen.length;
}

function readObject(text) {
  try {
    const value = JSON.parse(text);

    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
}
